import { DDSTypes } from "src/modules/3rd-party-management/constants";
import { INTERNATIONAL_DATE_FORMAT } from "src/common/constants";

const resolveCountryName = (country) => {
    if (!country) {
        return null;
    }

    if (typeof country === "string") {
        return country;
    }

    return country?.name || country?.label || null;
};

const formatGeolocationCoordinates = (geolocationReference = []) => {
    if (!Array.isArray(geolocationReference) || !geolocationReference.length) {
        return null;
    }

    const firstReference = geolocationReference[0];
    const geoJson = firstReference?.geoJSON || firstReference;
    const coordinates = geoJson?.geometry?.coordinates;

    if (!coordinates) {
        return `${geolocationReference.length} location(s)`;
    }

    if (geoJson.geometry.type === "Point" && Array.isArray(coordinates)) {
        return `${coordinates[1]}, ${coordinates[0]}`;
    }

    if (geoJson.geometry.type === "Polygon" && Array.isArray(coordinates?.[0]?.[0])) {
        const [longitude, latitude] = coordinates[0][0];
        return `${latitude}, ${longitude}`;
    }

    return `${geolocationReference.length} location(s)`;
};

const formatProductionArea = (product = {}) => {
    if (product?.productionArea) {
        return product.productionArea;
    }

    const geolocationReference = product?.geolocationReference;

    if (!Array.isArray(geolocationReference) || !geolocationReference.length) {
        return null;
    }

    const namedAreas = geolocationReference
        .map((reference) => reference?.name || reference?.label || reference?.areaName)
        .filter(Boolean);

    if (namedAreas.length) {
        return namedAreas.join(", ");
    }

    return `${geolocationReference.length} mapped area(s)`;
};

const formatCoordinates = (product = {}) => {
    if (product?.coordinates) {
        return product.coordinates;
    }

    return formatGeolocationCoordinates(product?.geolocationReference);
};

const formatDdsDeclarationType = (declarationType, t = (value) => value) => {
    if (!declarationType) {
        return null;
    }

    return t(DDSTypes[declarationType]?.title || declarationType);
};

const formatStatementValidation = (product = {}, t = (value) => value) => {
    if (product?.ddsDeclarationType !== "provide_reference_numbers") {
        return null;
    }

    if (product?.statementValidation === true) {
        return t("Valid");
    }

    if (product?.statementValidation === false) {
        return t("Invalid");
    }

    return t("Validating");
};

const formatGeoStatus = (product = {}, t = (value) => value) => {
    if (Array.isArray(product?.geolocationReference) && product.geolocationReference.length) {
        return t("Provided");
    }

    return t("Not Provided");
};

const formatProductName = (product = {}) => {
    return product?.productName || product?.name?.name || "-";
};

const formatNaceCode = (product = {}) => {
    const productName = product?.name;

    if (!productName) {
        return "-";
    }

    if (productName.naceCode) {
        return productName.naceCode;
    }

    if (productName.class && productName.name) {
        return `${productName.class} - ${productName.name}`;
    }

    return productName.class || "-";
};

const buildProductDetailRows = (products = [], { dateUtils, t = (value) => value, supplierCountry } = {}) => {
    if (!products.length) {
        return [{ title: "No Data", details: "-" }];
    }

    return products.flatMap((product, index) => {
        const prefix = `#${index + 1}`;
        const importDate = product?.importDate || product?.createdAt;
        const exportDate = product?.exportDate;

        return [
            { title: `${prefix} - ID`, details: product?.id },
            { title: `${prefix} - Product Name`, details: formatProductName(product) },
            { title: `${prefix} - NACE Code`, details: formatNaceCode(product) },
            { title: `${prefix} - Lot Number`, details: product?.lotNumber },
            { title: `${prefix} - Internal Product ID`, details: product?.internalProductId },
            {
                title: `${prefix} - Date of Import`,
                details: importDate && dateUtils
                    ? dateUtils.convertDateToDate(importDate, INTERNATIONAL_DATE_FORMAT)
                    : importDate,
            },
            {
                title: `${prefix} - Import Country`,
                details: resolveCountryName(product?.importFromCountry) || supplierCountry,
            },
            {
                title: `${prefix} - Date of Export`,
                details: exportDate && dateUtils
                    ? dateUtils.convertDateToDate(exportDate, INTERNATIONAL_DATE_FORMAT)
                    : exportDate,
            },
            { title: `${prefix} - Export Country`, details: resolveCountryName(product?.exportToCountry) },
            { title: `${prefix} - Quantity (Metric Tonnes)`, details: product?.metricTonnes },
            {
                title: `${prefix} - Country of Origin`,
                details: resolveCountryName(product?.originCountry) || supplierCountry,
            },
            {
                title: `${prefix} - DDS Declaration Type`,
                details: formatDdsDeclarationType(product?.ddsDeclarationType, t),
            },
            { title: `${prefix} - EUDR Product`, details: product?.is_eudr ? t("Yes") : t("No") },
            { title: `${prefix} - DDS Reference Number`, details: product?.ddsReferenceNumber },
            { title: `${prefix} - DDS Verification Number`, details: product?.ddsVerificationNumber },
            {
                title: `${prefix} - DDS Statement Status`,
                details: formatStatementValidation(product, t),
            },
            { title: `${prefix} - Geo Status`, details: formatGeoStatus(product, t) },
            { title: `${prefix} - HS Code (6-digit)`, details: product?.hsCode },
            { title: `${prefix} - KN Code (8-digit)`, details: product?.knCode },
            { title: `${prefix} - TARIC Code (10-digit)`, details: product?.taricCode },
            { title: `${prefix} - Container Number`, details: product?.containerNumber },
            { title: `${prefix} - Shipment Number`, details: product?.shipmentNumber },
            { title: `${prefix} - Description`, details: product?.description },
        ];
    });
};

const buildDueDiligenceRows = (products = [], { supplierCountry } = {}) => {
    if (!products.length) {
        return [{ title: "No Data", details: "-" }];
    }

    return products.map((product, index) => ({
        title: `ID ${product?.id || index + 1}`,
        details: [
            `Commodity: ${formatProductName(product)}`,
            `Country of Origin: ${resolveCountryName(product?.originCountry) || supplierCountry || "-"}`,
            `Production Area: ${formatProductionArea(product) || "-"}`,
            `Coordinates: ${formatCoordinates(product) || "-"}`,
            `Geo Status: ${Array.isArray(product?.geolocationReference) && product.geolocationReference.length ? "Provided" : "Not Provided"}`,
        ].join(" | "),
    }));
};

const buildProductSummaryRows = (products = []) => {
    if (!products.length) {
        return [{ title: "No Data", details: "-" }];
    }

    return products.map((product, index) => ({
        title: `#${index + 1} - ${formatProductName(product)}`,
        details: [
            `NACE: ${product?.name?.naceCode || product?.name?.class || "-"}`,
            `Lot: ${product?.lotNumber || "-"}`,
            `Description: ${product?.description || "-"}`,
        ].join(" | "),
    }));
};

export {
    buildProductDetailRows,
    buildDueDiligenceRows,
    buildProductSummaryRows,
    formatProductName,
};
